(function (root) {
  'use strict';

  class BonusError extends Error {
    constructor(code, message, cause) {
      super(message);
      this.name = 'HMLpBonusError';
      this.code = code;
      if (cause) this.cause = cause;
    }
  }

  const id = (value) => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? String(Number(value)) : null;
  const quantity = (value) => Number.isSafeInteger(value) && value >= 0;
  const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const fail = (code, message, cause) => new BonusError(code, message, cause);

  function normalizeConfig(value) {
    const config = isObject(value) ? value : {};
    const result = {
      enabled: config.enabled === true,
      productId: id(config.productId),
      variantId: id(config.variantId),
      variantIds: [...new Set((Array.isArray(config.variantIds) ? config.variantIds : [config.variantId]).map(id).filter(Boolean))],
      eligibility: config.eligibility === 'product' ? 'product' : 'source',
      source: typeof config.source === 'string' ? config.source : '',
      gift50: id(config.gift50),
      gift120: id(config.gift120),
      gift50Safe: config.gift50Safe === true,
      gift120Safe: config.gift120Safe === true,
      quantityPolicy: config.quantityPolicy,
    };
    result.valid = Boolean(result.productId && result.variantId && result.source && result.gift50 && result.gift120 &&
      result.gift50 !== result.gift120 && result.gift50 !== result.variantId && result.gift120 !== result.variantId &&
      !result.variantIds.includes(result.gift50) && !result.variantIds.includes(result.gift120) &&
      result.gift50Safe && result.gift120Safe && ['exact', 'cap120', 'pairs'].includes(result.quantityPolicy));
    return result;
  }

  function validateCart(cart) {
    if (!isObject(cart) || !Array.isArray(cart.items) || typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) {
      throw fail('BONUS_INVALID_CART', 'Nie udało się odczytać pełnego koszyka.');
    }
    const keys = new Set();
    cart.items.forEach((item) => {
      if (!isObject(item) || typeof item.key !== 'string' || !item.key || keys.has(item.key) ||
          !id(item.product_id) || !id(item.variant_id) || !quantity(item.quantity) || item.quantity === 0 ||
          (item.properties != null && !isObject(item.properties))) {
        throw fail('BONUS_INVALID_CART', 'Koszyk zawiera niepełną pozycję.');
      }
      keys.add(item.key);
    });
    return cart;
  }

  function eligible(item, config) {
    if (id(item.product_id) !== config.productId) return false;
    if (config.eligibility === 'product') return true;
    return id(item.variant_id) === config.variantId && item.properties && item.properties._noom_offer === config.source;
  }

  function gift(item, config) {
    const variant = id(item.variant_id);
    return variant === config.gift50 || variant === config.gift120;
  }

  function validateGift(item) {
    const prices = ['price', 'original_price', 'discounted_price', 'final_price', 'line_price', 'original_line_price', 'final_line_price'];
    if (item.price !== 0 || typeof item.requires_shipping !== 'boolean' || prices.some((field) => item[field] != null && item[field] !== 0)) {
      throw fail('BONUS_UNSAFE_GIFT', 'Bon musi kosztować 0 zł i mieć określony typ wysyłki.');
    }
  }

  function requiredGifts(count, policy) {
    if (policy === 'pairs') return { gift50: count % 2, gift120: Math.floor(count / 2) };
    if (policy === 'cap120') return { gift50: count === 1 ? 1 : 0, gift120: count >= 2 ? 1 : 0 };
    if (policy === 'exact') return { gift50: count === 1 ? 1 : 0, gift120: count === 2 ? 1 : 0 };
    throw fail('BONUS_INVALID_CONFIG', 'Reguła przyznawania bonu nie została ustawiona.');
  }

  function plan(cart, rawConfig) {
    const config = normalizeConfig(rawConfig);
    if (!config.valid) throw fail('BONUS_INVALID_CONFIG', 'Konfiguracja bonu jest niepełna.');
    validateCart(cart);
    let eligibleQuantity = 0;
    const groups = { gift50: [], gift120: [] };
    cart.items.forEach((item) => {
      if (eligible(item, config)) eligibleQuantity += item.quantity;
      if (gift(item, config)) {
        validateGift(item);
        groups[id(item.variant_id) === config.gift50 ? 'gift50' : 'gift120'].push(item);
      }
    });
    if (!quantity(eligibleQuantity)) throw fail('BONUS_INVALID_CART', 'Nieprawidłowa liczba produktów.');
    const required = requiredGifts(eligibleQuantity, config.quantityPolicy);
    const updates = Object.create(null);
    ['gift50', 'gift120'].forEach((kind) => {
      const lines = groups[kind];
      if (!lines.length && required[kind]) updates[config[kind]] = required[kind];
      lines.forEach((line, index) => {
        const expected = index === 0 ? required[kind] : 0;
        if (line.quantity !== expected) updates[line.key] = expected;
      });
    });
    return {
      eligibleQuantity,
      required,
      updates,
      changed: Object.keys(updates).length > 0,
      relevant: eligibleQuantity > 0 || groups.gift50.length > 0 || groups.gift120.length > 0,
    };
  }

  function canonicalIdentity(variant, properties, sellingPlan) {
    const variantId = id(variant);
    if (!variantId || !isObject(properties)) return null;
    const names = Object.keys(properties).filter((key) => properties[key] != null && properties[key] !== '').sort();
    if (names.some((key) => properties[key] != null && !['string', 'number', 'boolean'].includes(typeof properties[key]))) return null;
    return JSON.stringify({
      id: variantId,
      properties: names.map((key) => [key, properties[key] == null ? '' : String(properties[key])]),
      selling_plan: String(sellingPlan || ''),
    });
  }

  function formIdentity(form) {
    if (!form || typeof form.entries !== 'function') return null;
    const properties = Object.create(null);
    for (const [name, value] of form.entries()) {
      const match = /^properties\[(.*)\]$/.exec(name);
      if (match) {
        if (typeof value !== 'string') return null;
        properties[match[1]] = value;
      }
    }
    return canonicalIdentity(form.get('id'), properties, form.get('selling_plan'));
  }

  function itemIdentity(item) {
    if (!isObject(item)) return null;
    const allocation = item.selling_plan_allocation;
    const sellingPlan = allocation && allocation.selling_plan ? allocation.selling_plan.id : item.selling_plan;
    return canonicalIdentity(item.variant_id || item.id, item.properties || {}, sellingPlan);
  }

  // Product forms contain a mixture of add-line fields and metadata used by
  // Dawn to render the response. Only the former are sent as line items; any
  // unexpected field makes the caller use the ordinary add path.
  const addMetadata = new Set(['form_type', 'utf8', 'product-id', 'section-id', 'sections', 'sections_url']);
  const addFields = new Set(['id', 'quantity', 'selling_plan']);
  const dangerousPropertyNames = new Set(['__proto__', 'constructor', 'prototype']);

  function formEntries(form) {
    if (!form || typeof form.entries !== 'function') return null;
    let iterator;
    try { iterator = form.entries(); } catch (_) { return null; }
    if (!iterator || typeof iterator[Symbol.iterator] !== 'function') return null;
    const fields = [];
    try {
      for (const entry of iterator) {
        if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string' || typeof entry[1] !== 'string') return null;
        fields.push(entry);
      }
    } catch (_) { return null; }
    return fields;
  }

  function eligibleAddForm(fields, config) {
    const values = new Map();
    for (const [name, value] of fields) if (!values.has(name)) values.set(name, value);
    const productId = values.get('product-id') || values.get('product_id');
    if (productId && id(productId) !== config.productId) return false;
    const variant = id(values.get('id'));
    if (config.eligibility === 'product') return config.variantIds.includes(variant);
    return variant === config.variantId && values.get('properties[_noom_offer]') === config.source;
  }

  function prepareAddItems(form, config) {
    const fields = formEntries(form);
    if (!fields) return null;
    const seen = new Set();
    const properties = {};
    let variant = null;
    let rawQuantity;
    let sellingPlan;
    let hasSellingPlan = false;
    for (const [name, value] of fields) {
      const property = /^properties\[(.*)\]$/.exec(name);
      if (property) {
        const propertyName = property[1];
        if (dangerousPropertyNames.has(propertyName) || Object.prototype.hasOwnProperty.call(properties, propertyName)) return null;
        properties[propertyName] = value;
        continue;
      }
      if (addMetadata.has(name)) continue;
      if (!addFields.has(name) || seen.has(name)) return null;
      seen.add(name);
      if (name === 'id') variant = id(value);
      else if (name === 'quantity') rawQuantity = value;
      else { sellingPlan = value; hasSellingPlan = true; }
    }
    if (!variant) return null;
    const count = rawQuantity === undefined ? 1 : (/^\d+$/.test(rawQuantity) ? Number(rawQuantity) : NaN);
    if (!Number.isSafeInteger(count) || count <= 0) return null;
    if (!((config.eligibility === 'product' ? config.variantIds.includes(variant) : variant === config.variantId &&
      properties._noom_offer === config.source))) return null;
    const required = requiredGifts(count, config.quantityPolicy);
    if (!required.gift50 && !required.gift120) return null;
    const paid = { id: Number(variant), quantity: count };
    if (Object.keys(properties).length) paid.properties = properties;
    if (hasSellingPlan && sellingPlan !== '') paid.selling_plan = sellingPlan;
    const items = [paid];
    if (required.gift50) items.push({ id: Number(config.gift50), quantity: required.gift50 });
    if (required.gift120) items.push({ id: Number(config.gift120), quantity: required.gift120 });
    return { items };
  }

  function requestedQuantity(form) {
    const fields = formEntries(form);
    if (!fields) return null;
    let raw;
    for (const [name, value] of fields) {
      if (name !== 'quantity') continue;
      if (raw !== undefined) return null;
      raw = value;
    }
    const count = raw === undefined ? 1 : (/^\d+$/.test(raw) ? Number(raw) : NaN);
    return Number.isSafeInteger(count) && count > 0 ? count : null;
  }

  function createService(rawConfig, environment) {
    const config = normalizeConfig(rawConfig);
    const env = environment || root;
    const enabled = config.enabled && config.valid;
    let owner = null;
    let inFlight = false;
    let uncertain = false;
    let refreshRequired = false;
    let knownCart = null;
    try { if (rawConfig && rawConfig.cart) knownCart = validateCart(rawConfig.cart); } catch (_) { /* A fresh read is required. */ }

    const routeRoot = env.Shopify && env.Shopify.routes && env.Shopify.routes.root;
    const route = typeof routeRoot === 'string' && /^\/(?!\/)[^?#]*$/.test(routeRoot) ? routeRoot.replace(/\/?$/, '/') : '/';
    const fetcher = typeof env.fetch === 'function' ? env.fetch.bind(env) : null;
    const Timer = env.setTimeout || setTimeout;
    const clearTimer = env.clearTimeout || clearTimeout;
    const Controller = env.AbortController || (typeof AbortController !== 'undefined' && AbortController);

    async function fetchJSON(url, options, validate, deadlineAt) {
      if (!fetcher || !Controller) throw new Error('Przeglądarka nie obsługuje odczytu koszyka.');
      const remaining = deadlineAt == null ? 12000 : Math.min(12000, deadlineAt - Date.now());
      if (remaining <= 0) throw new Error('Upłynął czas odpowiedzi koszyka.');
      const controller = new Controller();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = Timer(() => { controller.abort(); reject(new Error('Upłynął czas odpowiedzi koszyka.')); }, remaining);
      });
      try {
        return await Promise.race([
          Promise.resolve().then(() => {
            if (deadlineAt != null && Date.now() >= deadlineAt) throw new Error('Upłynął czas odpowiedzi koszyka.');
            return fetcher(url, {
              credentials: 'same-origin', cache: 'no-store', ...options,
              signal: controller.signal,
            });
          }).then(async (response) => {
            const contentType = response && response.headers && response.headers.get('content-type');
            if (!response || !response.ok || !contentType || !/^(?:application\/json|(?:application|text)\/javascript)(?:\s*;|\s*$)/i.test(contentType)) {
              throw new Error('Koszyk nie zwrócił poprawnej odpowiedzi JSON.');
            }
            return validate(await response.json());
          }),
          timeout,
        ]);
      } finally { clearTimer(timer); }
    }

    function request(path, options, deadlineAt) {
      return fetchJSON(route + path, {
        ...options, headers: { Accept: 'application/json', ...(options && options.headers) },
      }, validateCart, deadlineAt);
    }

    function missingSections(cart, sections) {
      return sections.some((name) => !cart.sections || typeof cart.sections[name] !== 'string' || !cart.sections[name].trim());
    }

    function readSections(sectionUrl, sections, deadlineAt) {
      const url = new URL(sectionUrl, 'https://section.invalid');
      url.searchParams.set('sections', sections.join(','));
      return fetchJSON(url.pathname + url.search, {}, (result) => {
        if (!isObject(result) || sections.some((name) => typeof result[name] !== 'string' || !result[name].trim())) {
          throw new Error('Nie udało się odczytać widoku koszyka.');
        }
        return result;
      }, deadlineAt);
    }

    function relevant() {
      return Boolean(enabled && knownCart && knownCart.items.some((item) => eligible(item, config) || gift(item, config)));
    }

    return {
      enabled,
      get active() { return Boolean(owner || inFlight); },
      config: Object.freeze({ ...config }),
      get currentCart() { return knownCart; },
      get relevant() { return relevant(); },
      begin() {
        if (!enabled || owner || inFlight) return null;
        owner = Object.freeze({});
        return owner;
      },
      end(token) { if (token && token === owner) owner = null; },
      isEligibleForm(form) {
        if (!enabled || !form || typeof form.get !== 'function') return false;
        const productId = form.get('product-id') || form.get('product_id');
        if (productId && id(productId) !== config.productId) return false;
        if (config.eligibility === 'product') return config.variantIds.includes(id(form.get('id')));
        return id(form.get('id')) === config.variantId && form.get('properties[_noom_offer]') === config.source;
      },
      prepareAdd(form) {
        if (!enabled || !owner || inFlight || uncertain || refreshRequired || !knownCart) return null;
        try {
          if (validateCart(knownCart).items.length !== 0) return null;
          const fields = formEntries(form);
          if (!fields) return null;
          // Keep eligibility semantics identical to the public form guard,
          // while parsing the same immutable entry snapshot for the payload.
          if (!eligibleAddForm(fields, config)) return null;
          return prepareAddItems({ entries: () => fields }, config);
        } catch (_) { return null; }
      },
      confirmsAdd(form, cart) {
        if (!enabled) return false;
        try {
          const fullCart = validateCart(cart);
          const requested = requestedQuantity(form);
          const target = formIdentity(form);
          if (!requested || !target) return false;
          let total = 0;
          for (const item of fullCart.items) {
            if (itemIdentity(item) !== target) continue;
            if (total > Number.MAX_SAFE_INTEGER - item.quantity) return false;
            total += item.quantity;
          }
          return total >= requested;
        } catch (_) { return false; }
      },
      identity: formIdentity,
      identityOfItem: itemIdentity,
      async reconcile(options) {
        if (!enabled) throw fail('BONUS_INACTIVE', 'Obsługa bonu jest wyłączona.');
        if (!owner || inFlight) throw fail('BONUS_BUSY', 'Koszyk jest już aktualizowany.');
        const token = owner;
        const settings = options || {};
        const deadlineAt = Number.isFinite(settings.deadlineAt) ? settings.deadlineAt : null;
        const sections = [...new Set(Array.isArray(settings.sections) ? settings.sections.filter((name) => typeof name === 'string' && /^[\w-]+$/.test(name)) : [])].slice(0, 5);
        const sectionUrl = typeof settings.sectionsUrl === 'string' && /^\/(?!\/)/.test(settings.sectionsUrl) ? settings.sectionsUrl : route + 'cart';
        inFlight = true;
        try {
          let cart;
          if (settings.cart && !uncertain && !refreshRequired) {
            try { cart = validateCart(settings.cart); } catch (_) { /* Add responses need a fresh full cart. */ }
          }
          if (!cart) {
            try { cart = await request('cart.js', undefined, deadlineAt); }
            catch (error) { throw fail('BONUS_READ_FAILED', 'Nie udało się sprawdzić koszyka. Spróbuj ponownie.', error); }
          }
          knownCart = cart;
          uncertain = false;
          refreshRequired = false;
          const adjustment = plan(cart, config);
          if (adjustment.changed) {
            if (owner !== token) throw fail('BONUS_BUSY', 'Operacja koszyka została zakończona.');
            const body = { updates: adjustment.updates };
            if (sections.length) { body.sections = sections; body.sections_url = sectionUrl; }
            uncertain = true;
            try {
              cart = await request('cart/update.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
              }, deadlineAt);
              knownCart = cart;
              const result = plan(cart, config);
              if (result.changed) throw new Error('Stan bonu po zapisie wymaga ponownego odczytu.');
              uncertain = false;
            } catch (error) {
              if (error.code === 'BONUS_UNSAFE_GIFT') throw error;
              throw fail('BONUS_UNCERTAIN', 'Nie udało się potwierdzić bonu. Sprawdź koszyk ponownie.', error);
            }
          }
          if (missingSections(cart, sections)) {
            try {
              const rendered = await readSections(sectionUrl, sections, deadlineAt);
              cart = { ...cart, sections: { ...cart.sections, ...rendered } };
              knownCart = cart;
            } catch (error) {
              refreshRequired = true;
              throw fail('BONUS_SECTIONS_FAILED', 'Nie udało się odświeżyć widoku koszyka. Spróbuj ponownie.', error);
            }
          }
          return {
            ...cart,
            bonusSectionsMissing: false,
            bonusAdjusted: adjustment.changed,
          };
        } finally { inFlight = false; }
      },
    };
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { BonusError, normalizeConfig, validateCart, requiredGifts, plan, formIdentity, itemIdentity, createService };
  } else if (root && !root.HMLpBonus) {
    let config = {};
    const element = root.document && root.document.getElementById('hm-lp-bonus-config');
    try { if (element) config = JSON.parse(element.textContent); } catch (_) { /* Invalid config disables the service. */ }
    root.HMLpBonus = createService(config, root);
  }
})(typeof window !== 'undefined' ? window : globalThis);
